import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
} from '@learnforge/demo-render';
import { createCartesianViewport, createFormulaLegend } from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { parse, evaluate } from '@learnforge/demo-math/expr';
import { formatNumber } from '@learnforge/demo-math/numeric';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { createSlopeTicks, type SlopeTicksHandle } from '../render/slope-ticks';
import { eulerCurve } from '../calc';
import { SLOPE_FIELD_PRESETS, type SlopeFieldPreset } from '../data/slope-field-presets';
import { createCartesianPlane, type CartesianPlaneHandle } from '../render/cartesian-plane';

// ─── Stage layout constants ───────────────────────────────────────────────────

/** Pixels per world unit. */
const UNIT = 60;
/** Stage dimensions in pixels. */
const STAGE_W = 800;
const STAGE_H = 460;
const MATH_ORIGIN_Y = STAGE_H / 2 / UNIT; // ≈ 3.83
const BASE_Y_DOMAIN: [number, number] = [-MATH_ORIGIN_Y, MATH_ORIGIN_Y];
const PLOT_RECT = { x: 0, y: 0, width: STAGE_W, height: STAGE_H };
/** Slope-tick density (per axis). */
const TICK_DENSITY = 14;
/** Number of sample points for the closed-form solution overlay. */
const SOLUTION_SAMPLE_N = 400;
/** Grid of y offsets used when drawing the family of solution curves. */
const FAMILY_Y_OFFSETS = [-2, -1, 0, 1, 2];

// ─── State ────────────────────────────────────────────────────────────────────

export interface SlopeFieldLabState {
  presetId: string;
  start: Vec2;
  stepH: number;
  showFamily: boolean;
}

// ─── Pure helpers (exported and tested) ──────────────────────────────────────

/**
 * Compile a 2-variable slope function from a preset's `g` expression string.
 *
 * `compileFn` only binds `x`, so we parse the node once and build a proper
 * 2-variable closure that binds both `x` and `y` on every call.
 */
function buildGFn(preset: SlopeFieldPreset): (x: number, y: number) => number {
  const node = parse(preset.g).node;
  return (x: number, y: number) => evaluate(node, { x, y });
}

/**
 * Truncate an Euler-curve half at the first blow-up or domain-escape point.
 *
 * Two complementary checks (either triggers truncation):
 *
 * 1. **Jump check** — if the y-step between consecutive points exceeds
 *    `h * JUMP_SLOPE_LIMIT`, the integrator has hit a near-singularity and
 *    jumped wildly. Keep the point *before* the jump (it's still on-curve).
 *    `JUMP_SLOPE_LIMIT = 50` means a single step of `|dy| > 50*h` is a blow-up.
 *    For `h = 0.05` that fires at `|dy| > 2.5`; for smooth fields (g=y near
 *    origin the slope is ~1, so `|dy| ≈ 0.05`) this never triggers.
 *
 * 2. **Domain-escape check** — after the curve has reached the active visible
 *    y-domain, keep its first point beyond that domain so SVG clipping can draw
 *    the boundary crossing, then drop everything after it. Curves whose initial
 *    condition starts outside a vertically panned view may still enter it.
 *
 * `eulerCurve` is kept generic (finite-only guard); truncation lives here so
 * the experiment controls the policy.
 *
 * @param pts  The raw array returned by `eulerCurve` (at least one point).
 * @param h    The unsigned step size used to produce `pts`.
 * @param visibleYDomain The active viewport's mathematical y bounds.
 * @returns    A prefix of `pts` that stays within bounds.
 */
function truncateAtBlow(pts: Vec2[], h: number, visibleYDomain: [number, number]): Vec2[] {
  const JUMP_SLOPE_LIMIT = 50;
  const [visibleYMin, visibleYMax] = visibleYDomain;
  let reachedVisibleDomain =
    pts[0] !== undefined && pts[0].y >= visibleYMin && pts[0].y <= visibleYMax;

  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1]!;
    const curr = pts[i]!;

    const jumpY = Math.abs(curr.y - prev.y);
    if (jumpY > JUMP_SLOPE_LIMIT * h) {
      // Keep everything up to and including prev (last clean point).
      return pts.slice(0, i);
    }

    const isVisible = curr.y >= visibleYMin && curr.y <= visibleYMax;
    if (isVisible) {
      reachedVisibleDomain = true;
    } else if (reachedVisibleDomain) {
      // Retain the first outside point so the clipped segment reaches the pane edge.
      return pts.slice(0, i + 1);
    }
  }

  return pts;
}

/**
 * Build the solution curve through `start` for the given state.
 *
 * Runs Euler forward (positive h) and backward (negative h) from `start`,
 * reverses the backward half (so x is monotonically increasing), then
 * concatenates: `[...backward_reversed, ...forward]` gives a single array
 * ordered by increasing x through the start point.
 *
 * Non-finite values are handled inside `eulerCurve` (it stops early).
 * Near-singularity blow-ups (finite but huge steps) are handled by
 * `truncateAtBlow`, which clips each half to the visible, well-behaved
 * portion before they are joined.
 */
export function solutionThrough(
  state: SlopeFieldLabState,
  start: Vec2,
  visibleXDomain?: [number, number],
  visibleYDomain: [number, number] = BASE_Y_DOMAIN,
): Vec2[] {
  const preset =
    SLOPE_FIELD_PRESETS.find((p) => p.id === state.presetId) ?? SLOPE_FIELD_PRESETS[0]!;
  const g = buildGFn(preset);
  const h = Math.max(0.001, state.stepH);
  const domain = visibleXDomain ?? preset.domain;
  const forwardSteps = Math.max(0, Math.ceil((domain[1] - start.x) / h));
  const backwardSteps = Math.max(0, Math.ceil((start.x - domain[0]) / h));

  const forward = truncateAtBlow(eulerCurve(g, start, h, forwardSteps), h, visibleYDomain);
  const backward = truncateAtBlow(eulerCurve(g, start, -h, backwardSteps), h, visibleYDomain);

  // The backward array starts at `start` and goes in the -x direction.
  // Reverse it (so it's ordered from most-negative x toward start) and drop
  // the duplicated start point (index 0 of backward = start = index 0 of forward).
  const backwardReversed = backward.slice(1).reverse();

  return [...backwardReversed, ...forward];
}

interface ExactSolutionModel {
  formula: string;
  valueAt(x: number): number;
}

function compactNumber(value: number): string {
  return formatNumber(value, { decimals: 3 }).replace(/(?:\.0+|(\.\d*?)0+)$/, '$1');
}

/** Build the exact solution through one initial condition for supported presets. */
function exactSolutionModel(preset: SlopeFieldPreset, start: Vec2): ExactSolutionModel | null {
  if (!preset.solution) return null;
  const solNode = parse(preset.solution).node;
  const particularValueAt = (x: number): number => evaluate(solNode, { x });

  if (preset.id === 'exponential-growth') {
    const coefficient = start.y / particularValueAt(start.x);
    const formula =
      Math.abs(coefficient) < 1e-12
        ? 'y(x) = 0'
        : Math.abs(coefficient - 1) < 1e-12
          ? `y(x) = ${preset.solution}`
          : Math.abs(coefficient + 1) < 1e-12
            ? `y(x) = −${preset.solution}`
            : `y(x) ≈ ${compactNumber(coefficient)} * ${preset.solution}`;
    return {
      formula,
      valueAt: (x: number) => coefficient * particularValueAt(x),
    };
  }

  const constant = start.y - particularValueAt(start.x);
  const formula =
    Math.abs(constant) < 1e-12
      ? `y(x) = ${preset.solution}`
      : `y(x) ≈ ${preset.solution} ${constant < 0 ? '−' : '+'} ${compactNumber(Math.abs(constant))}`;
  return {
    formula,
    valueAt: (x: number) => particularValueAt(x) + constant,
  };
}

// ─── Slope-field g with singularity safety ───────────────────────────────────

/**
 * Wrap the raw g function so that NaN results (e.g. 0/0 at the origin for
 * the circles preset) are converted to a clean vertical slope (±Infinity)
 * or skipped. The slope-ticks layer uses atan(m), so ±Infinity → ±π/2
 * (vertical tick) which is correct. NaN would produce a broken tick.
 *
 * We do NOT modify ±Infinity results — those are already handled correctly
 * by atan (they produce a vertical line, which is exact for circles).
 */
function safeG(rawG: (x: number, y: number) => number): (x: number, y: number) => number | null {
  return (x: number, y: number): number | null => {
    const m = rawG(x, y);
    if (Number.isNaN(m)) {
      // Exact singular point: skip this lattice tick entirely.
      return null;
    }
    return m;
  };
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<SlopeFieldLabState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let plane: CartesianPlaneHandle;
  let figure: FigureHandle;
  let slopeTicks: SlopeTicksHandle;
  let curves: CurveLayerHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let legend: FormulaLegendHandle;
  let activePresetId = SLOPE_FIELD_PRESETS[0]!.id;

  function defaultState(_ctx: CalculusContext): SlopeFieldLabState {
    return {
      presetId: SLOPE_FIELD_PRESETS[0]!.id,
      start: { x: 0, y: 1 },
      stepH: 0.05,
      showFamily: false,
    };
  }

  function applyPresetLocal(state: SlopeFieldLabState, id: string): SlopeFieldLabState {
    const preset = SLOPE_FIELD_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    return { ...state, presetId: preset.id, start: { x: 0, y: 1 } };
  }

  function getPreset(state: SlopeFieldLabState): SlopeFieldPreset {
    return SLOPE_FIELD_PRESETS.find((p) => p.id === state.presetId) ?? SLOPE_FIELD_PRESETS[0]!;
  }

  /**
   * Draw the slope field directly in the currently visible mathematical domain.
   */
  function drawSlopeField(
    handle: SlopeTicksHandle,
    preset: SlopeFieldPreset,
    xDomain: [number, number],
    yDomain: [number, number],
    density: number,
  ): void {
    const rawG = buildGFn(preset);
    const safeWrapped = safeG(rawG);

    handle.setField(
      (x: number, y: number): number => {
        const result = safeWrapped(x, y);
        if (result === null) {
          // NaN singularity (e.g. 0/0 at origin for circles): force vertical tick.
          // atan(Infinity) = π/2 → correct visual for a vertical slope.
          return Infinity;
        }
        return result;
      },
      xDomain,
      yDomain,
      density,
    );
  }

  return {
    labelKey: 'experiment.slope-field-lab',
    demoId: 'calculus',
    advancedKey: 'slope-field-lab',

    initialState(ctx): SlopeFieldLabState {
      return defaultState(ctx);
    },

    applyPreset(state, id): SlopeFieldLabState {
      return applyPresetLocal(state, id);
    },

    setup(ctx, controller: HarnessController<SlopeFieldLabState>): void {
      const base = { xDomain: SLOPE_FIELD_PRESETS[0]!.domain, yDomain: BASE_Y_DOMAIN };
      plane = createCartesianPlane(controller.svg, { domains: base, plotRect: PLOT_RECT });
      controller.registerCleanup(() => plane.destroy());

      // Slope-tick layer: sits just above the grid background
      slopeTicks = createSlopeTicks(controller.svg, plane);
      controller.registerCleanup(() => slopeTicks.destroy());

      // Curve layer: solution curve(s) drawn above the ticks
      curves = createCurveLayer(controller.svg, plane);
      controller.registerCleanup(() => curves.destroy());

      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());
      viewport = createCartesianViewport(controller.svg, {
        base,
        plotRect: PLOT_RECT,
        onChange(next) {
          plane.setDomains(next);
          controller.requestRender();
        },
      });
      controller.registerCleanup(() => viewport.dispose());
    },

    render(state: SlopeFieldLabState, ctx): void {
      const preset = getPreset(state);
      if (preset.id !== activePresetId) {
        const base = { xDomain: preset.domain, yDomain: BASE_Y_DOMAIN };
        activePresetId = preset.id;
        viewport.setBase(base, { notify: false });
        plane.setDomains(base);
      }
      const domains = plane.getDomains();

      // ── Slope-tick field ──
      drawSlopeField(slopeTicks, preset, domains.xDomain, domains.yDomain, TICK_DENSITY);

      // ── Primary solution curve ──
      const solution = solutionThrough(state, state.start, domains.xDomain, domains.yDomain);
      curves.setPolyline('solution', solution, 'calc-curve-primary');

      // ── Closed-form overlay (faint) ──
      let solutionFormula = `y′ = ${preset.g}, y(${compactNumber(state.start.x)}) ≈ ${compactNumber(state.start.y)}`;
      const exactSolution = exactSolutionModel(preset, state.start);
      if (exactSolution) {
        solutionFormula = exactSolution.formula;
        const [xLo, xHi] = domains.xDomain;
        const step = (xHi - xLo) / SOLUTION_SAMPLE_N;
        const exactPts: Vec2[] = [];
        for (let i = 0; i <= SOLUTION_SAMPLE_N; i++) {
          const mx = xLo + i * step;
          const my = exactSolution.valueAt(mx);
          if (Number.isFinite(my)) {
            exactPts.push({ x: mx, y: my });
          } else {
            // Insert a break in the path
            exactPts.push({ x: NaN, y: NaN });
          }
        }
        curves.setPolyline('solution-exact', exactPts, 'calc-curve-faint');
      } else {
        curves.clear('solution-exact');
      }

      // ── Family of solution curves (advanced) ──
      if (ctx.advanced && state.showFamily) {
        FAMILY_Y_OFFSETS.forEach((yOff, idx) => {
          const familyStart: Vec2 = { x: state.start.x, y: state.start.y + yOff };
          const fam = solutionThrough(state, familyStart, domains.xDomain, domains.yDomain);
          curves.setPolyline(`family-${idx}`, fam, 'calc-curve-secondary');
        });
      } else {
        // Clear any previously-drawn family curves
        FAMILY_Y_OFFSETS.forEach((_, idx) => {
          curves.clear(`family-${idx}`);
        });
      }

      // ── Figure: draggable initial-condition point ──
      const screenStart = plane.worldToScreen(state.start);
      const elements: FigureElement[] = [
        {
          kind: 'point',
          id: 'pt-start',
          at: screenStart,
          style: { className: 'geo-point-draggable' },
          draggable: {},
        },
      ];
      figure.update(elements);
      curves.group.ownerSVGElement
        ?.querySelector('[data-figure-id="pt-start"]')
        ?.setAttribute('data-no-pan', 'true');
      legend.setEntries([
        {
          id: 'field',
          formula: `dy/dx = ${preset.g}`,
          color: 'var(--ink-soft)',
          lineStyle: 'dotted',
        },
        {
          id: 'solution',
          formula: solutionFormula,
          color: 'var(--accent)',
        },
      ]);

      // ── Readout ──
      const eulerError = computeEulerError(preset, state);
      readout.update([
        {
          id: 'slope-eq',
          lhs: 'dy/dx',
          relation: '=',
          rhs: preset.g,
        },
        {
          id: 'start-pt',
          lhs: 'y₀',
          relation: '=',
          rhs: `(${state.start.x.toFixed(2)}, ${state.start.y.toFixed(2)})`,
        },
        {
          id: 'step-h',
          lhs: 'h',
          relation: '=',
          rhs: state.stepH.toFixed(3),
        },
        ...(eulerError !== null
          ? [
              {
                id: 'euler-error',
                lhs: '|Euler − exact|',
                relation: '≈' as const,
                rhs: eulerError.toExponential(2),
                emphasis: eulerError < 0.01 ? ('invariant' as const) : ('neutral' as const),
              },
            ]
          : []),
      ]);
    },

    onDrag(state: SlopeFieldLabState, pointId: string, at: Vec2): SlopeFieldLabState {
      if (pointId === 'pt-start') {
        return { ...state, start: plane.screenToWorld(at) };
      }
      return state;
    },

    panel(state: SlopeFieldLabState, ctx): PanelRow<CalcKey, SlopeFieldLabState>[] {
      const rows: PanelRow<CalcKey, SlopeFieldLabState>[] = [];

      // Preset dropdown
      rows.push({
        kind: 'dropdown',
        id: 'slope-field-preset',
        labelKey: 'slope-field.panel.preset',
        options: SLOPE_FIELD_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // Advanced toggle
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      // Advanced: step-h slider (smaller h → more accurate Euler)
      if (ctx.advanced) {
        rows.push({
          kind: 'slider',
          id: 'slope-field-step-h',
          labelKey: 'slope-field.panel.step-h',
          min: 0.005,
          max: 0.2,
          step: 0.005,
          value: state.stepH,
          format: (v) => v.toFixed(3),
          onChange: (v) => ({ ...state, stepH: v }),
        });

        rows.push({
          kind: 'toggle',
          labelKey: 'slope-field.panel.show-family',
          value: state.showFamily,
          onChange: (v) => ({ ...state, showFamily: v }),
        });
      }

      return rows;
    },

    actions: (_state: SlopeFieldLabState, ctx): ActionBarSpec<CalcKey, SlopeFieldLabState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * SLOPE_FIELD_PRESETS.length);
          const preset = SLOPE_FIELD_PRESETS[idx]!;
          return { ...s, presetId: preset.id, start: { x: 0, y: 1 } };
        },
        presets: SLOPE_FIELD_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: SlopeFieldLabState, _shotId: string): SlopeFieldLabState {
      return {
        ...state,
        start: { x: 0, y: 1 },
        stepH: 0.05,
      };
    },
  };
}

// ─── Euler error helper ───────────────────────────────────────────────────────

/**
 * Compute the Euler error at x = start.x + 1 vs the closed-form solution,
 * when a solution is available in the preset. Returns null otherwise.
 */
function computeEulerError(preset: SlopeFieldPreset, state: SlopeFieldLabState): number | null {
  const exactSolution = exactSolutionModel(preset, state.start);
  if (!exactSolution) return null;

  // Run Euler forward 1 unit
  const g = buildGFn(preset);
  const target = state.start.x + 1;
  const steps = Math.round(1 / Math.max(0.001, state.stepH));
  const pts = eulerCurve(g, state.start, state.stepH, steps);
  const endPt = pts[pts.length - 1];
  if (!endPt || !Number.isFinite(endPt.y)) return null;

  const exact = exactSolution.valueAt(target);
  if (!Number.isFinite(exact)) return null;

  return Math.abs(endPt.y - exact);
}

export const experiment = defineExperiment(buildSpec());
